import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { StatusBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import { useApi } from "../../hooks/useApi";
import { useRefresh } from "../../contexts/RefreshContext";
import { listMyTickets } from "../../api/myTicketService";
import { listMyFiles } from "../../api/fileService";
import { paginatedRows } from "../../utils/pagination";
import { formatDateShort, ticketNumber } from "../../utils/formatters";
import { useAuth } from "../../contexts/AuthContext";

export default function OverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshKey } = useRefresh();
  const tickets = useApi(() => listMyTickets({ per_page: 20 }), [refreshKey]);
  const files = useApi(() => listMyFiles({ per_page: 20 }), [refreshKey]);
  const ticketRows = paginatedRows(tickets.data);
  const fileRows = paginatedRows(files.data);
  const active = ticketRows.filter((t) => t.status === "open" || t.status === "in_progress").length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Hello{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</h1>
          <div className="page-sub">Here's what's happening with your account</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => navigate("/portal/tickets/new")}>
            <Icon name="plus" size={14} /> Raise a ticket
          </button>
        </div>
      </div>

      <div className="grid-3">
        <button className="card clickable" style={{ textAlign: "left", border: "1px solid var(--border)" }} onClick={() => navigate("/portal/files")}>
          <div className="card-body row gap-md">
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--primary-50)", color: "var(--primary-700)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="folder" size={20} />
            </div>
            <div>
              <div className="t-primary fw-600">{fileRows.length} files</div>
              <div className="t-muted text-xs">Click to view documents</div>
            </div>
          </div>
        </button>
        <button className="card clickable" style={{ textAlign: "left", border: "1px solid var(--border)" }} onClick={() => navigate("/portal/tickets")}>
          <div className="card-body row gap-md">
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--info-100)", color: "var(--info)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="message-circle" size={20} />
            </div>
            <div>
              <div className="t-primary fw-600">{ticketRows.length} tickets</div>
              <div className="t-muted text-xs">{active} active</div>
            </div>
          </div>
        </button>
        <div className="card">
          <div className="card-body row gap-md">
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "var(--success-100)", color: "var(--success)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="check-circle" size={20} />
            </div>
            <div>
              <div className="t-primary fw-600">All filings on track</div>
              <div className="t-muted text-xs">Your CA team is on it</div>
            </div>
          </div>
        </div>
      </div>

      <div className="spacer-lg" />

      <div className="grid-main-rail">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Recent files</div>
              <div className="card-sub">Shared by your CA team</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/portal/files")}>
              View all <Icon name="arrow-right" size={12} />
            </button>
          </div>
          <div className="card-body no-pad">
            {files.loading ? (
              <div style={{ padding: 16 }}><LoadingBlock rows={3} /></div>
            ) : fileRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--ink-500)" }}>No files yet</div>
            ) : (
              <table className="table">
                <thead><tr><th>Name</th><th>Size</th><th>Uploaded</th></tr></thead>
                <tbody>
                  {fileRows.slice(0, 6).map((f) => (
                    <tr key={f.id}>
                      <td className="t-primary">{f.name || f.original_name}</td>
                      <td className="t-muted">{f.size_human || `${Math.round((f.size || 0) / 1024)} KB`}</td>
                      <td className="t-muted">{formatDateShort(f.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Open tickets</div>
              <div className="card-sub">Conversations with your CA</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/portal/tickets")}>
              View all <Icon name="arrow-right" size={12} />
            </button>
          </div>
          <div style={{ padding: 0 }}>
            {tickets.loading ? (
              <div style={{ padding: 16 }}><LoadingBlock rows={3} /></div>
            ) : ticketRows.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--ink-500)" }}>No tickets yet</div>
            ) : (
              ticketRows.slice(0, 4).map((t) => (
                <div
                  key={t.id}
                  style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", cursor: "pointer" }}
                  onClick={() => navigate(`/portal/tickets/${t.id}`)}
                >
                  <div className="row" style={{ justifyContent: "space-between" }}>
                    <div className="t-primary text-sm fw-500" style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {t.subject}
                    </div>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="t-muted text-xs" style={{ marginTop: 4 }}>
                    {ticketNumber(t.id)} · {formatDateShort(t.updated_at)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
}
